import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Award, FileDown, KeyRound, Lock, Medal, Plus, Send, X, ZoomIn, ZoomOut } from "lucide-react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_SIZE = 5;

// group: 0 = Militare, 1 = Croce Rossa & Emergenza, 2 = Studio & Formazione
const featured = { img: "/images/attestati/medaglia-nato-balcani-2003.webp", title: "Medaglia NATO Non Article 5", org: "NATO · Operazione nei Balcani", year: "2003" };
const featuredMedal = { img: "/images/attestati/medaglia-nato-foto.webp", title: "Medaglia NATO Non Article 5", org: "NATO · Operazione nei Balcani", year: "2003" };

const certs = [
  { img: "/images/attestati/operazione-domino-2003.webp", title: "Operazione \"Domino 2003\"", org: "Ministero della Difesa · Brigata Alpina \"Julia\" — 8° Reggimento Alpini", year: "2003", group: 0 },
  { img: "/images/attestati/foglio-congedo-2004.webp", title: "Foglio di Congedo Illimitato", org: "Brigata Alpina \"Julia\" · 8° Reggimento Alpini", year: "2004", group: 0 },
  { img: "/images/attestati/medaglia-domino-foto.webp", title: "Medaglia Operazione \"Domino 2003\"", org: "Esercito Italiano · foto originale", year: "2003", group: 0 },
  { img: "/images/attestati/medaglia-pace-foto.webp", title: "Medaglia Operazioni di Pace", org: "Esercito Italiano · foto originale", year: "2003", group: 0 },
  { img: "/images/attestati/volontario-soccorso-2002.webp", title: "Volontario del Soccorso", org: "Croce Rossa Italiana · Comitato di Mede", year: "2002", group: 1 },
  { img: "/images/attestati/operatore-nbcr-2005.webp", title: "Operatore N.B.C.R.", org: "CRI · Comitato Regionale del Piemonte", year: "2005", group: 1 },
  { img: "/images/attestati/nbcr-sanitario-2005.webp", title: "Operatore N.B.C.R. — Specializzazione Sanitaria", org: "CRI · Comitato Regionale del Piemonte", year: "2005", group: 1 },
  { img: "/images/attestati/soccorritore-118-2006.webp", title: "Volontario Soccorritore 118", org: "Regione Piemonte · Croce Rossa Italiana", year: "2006", group: 1 },
  { img: "/images/attestati/istruttore-protezione-civile-2006.webp", title: "Istruttore di Protezione Civile", org: "CRI · Comitato Regionale Toscana", year: "2006", group: 1 },
  { img: "/images/attestati/heat-watch-2006.webp", title: "Heat Health Watch Warning System", org: "CRI Alessandria · Ministero della Salute", year: "2006", group: 1 },
  { img: "/images/attestati/catastrofi-sociali-2006.webp", title: "Le Catastrofi Sociali", org: "CRI · Comitato di Alessandria", year: "2006", group: 1 },
  { img: "/images/attestati/diritto-internazionale-2006.webp", title: "Diritto Internazionale Umanitario", org: "CRI Bergamo · Comitato di Caravaggio", year: "2006", group: 1 },
  { img: "/images/attestati/igiene-vaccinazioni-2006.webp", title: "Igiene Personale e Vaccinazioni in Emergenza", org: "CRI · Comitato di Alessandria", year: "2006", group: 1 },
  { img: "/images/attestati/sala-operativa-2006.webp", title: "Addetti alle Sale Operative in Emergenza", org: "CRI · Comitato di Alessandria", year: "2006", group: 1 },
  { img: "/images/attestati/medaglia-covid-foto.webp", title: "Medaglia Emergenza Covid-19", org: "Protezione Civile · foto originale", year: "2020", group: 1 },
  { img: "/images/attestati/distintivo-cruz-roja.webp", title: "Distintivo Voluntario", org: "Cruz Roja · Spagna · foto originale", year: "", group: 1 },
  { img: "/images/attestati/distintivo-protezione-civile.webp", title: "Distintivo Protezione Civile — Volontariato", org: "Protezione Civile · Italia · foto originale", year: "", group: 1 },
  { img: "/images/attestati/diploma-maserati-2005.webp", title: "Diploma P.I. Capo Tecnico — Elettronica e Telecomunicazioni", org: "ITIS A. Maserati · Voghera", year: "2005", group: 2 },
  { img: "/images/attestati/disaster-management-2005.webp", title: "Operatore Qualificato in Disaster Management", org: "Regione Piemonte · Dipartimento Protezione Civile", year: "2005", group: 2 },
  { img: "/images/attestati/qualifica-oss-2007.webp", title: "Qualifica di Operatore Socio-Sanitario", org: "Enaip Lombardia · Regione Lombardia", year: "2007", group: 2 },
  { img: "/images/attestati/laurea-ecampus-2010.webp", title: "Laurea in Economia e Commercio", org: "Università degli Studi e-Campus · Novedrate", year: "2010", group: 2 },
];

const CertImage = ({ cert, className }) => (
  <img src={cert.img} alt={`${cert.title} — ${cert.org} (${cert.year})`} loading="lazy" draggable={false}
    className={`object-contain pointer-events-none select-none ${className}`} />
);

export default function Attestati() {
  const { t } = useLang();
  const a = t.attestati;
  const [visibleMap, setVisibleMap] = useState({});
  const [zoomCert, setZoomCert] = useState(null);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem("attestati_unlocked") === "1");
  const [pw, setPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState("");
  const [reqName, setReqName] = useState("");
  const [reqEmail, setReqEmail] = useState("");
  const [reqMsg, setReqMsg] = useState("");
  const [reqBusy, setReqBusy] = useState(false);
  const [reqDone, setReqDone] = useState(false);

  const unlock = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    setPwError("");
    try {
      await axios.post(`${API}/attestati/unlock`, { password: pw });
      sessionStorage.setItem("attestati_unlocked", "1");
      setUnlocked(true);
    } catch {
      setPwError(a.unlockError);
    } finally {
      setPwBusy(false);
    }
  };

  const sendAccessRequest = async (e) => {
    e.preventDefault();
    setReqBusy(true);
    try {
      await axios.post(`${API}/attestati/access-request`, { name: reqName, email: reqEmail, message: reqMsg });
      setReqDone(true);
    } catch {
      toast.error(a.reqErr);
    } finally {
      setReqBusy(false);
    }
  };

  const openZoom = (cert) => { setZoomCert(cert); setScale(1); setPos({ x: 0, y: 0 }); };
  const closeZoom = useCallback(() => setZoomCert(null), []);

  useEffect(() => {
    if (!zoomCert) return;
    const onKey = (e) => e.key === "Escape" && closeZoom();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [zoomCert, closeZoom]);

  const onPointerDown = (e) => { drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }; };
  const onPointerMove = (e) => { if (drag.current) setPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }); };
  const onPointerUp = () => { drag.current = null; };

  const groups = [0, 1, 2].map((g) => ({ g, items: certs.filter((c) => c.group === g) }));

  return (
    <main className="min-h-screen" data-testid="attestati-page">
      <Seo
        title="Titoli e Diplomi — Yuri Selvaggio"
        description="Gli attestati e i diplomi conseguiti da Yuri Selvaggio: Croce Rossa Italiana, protezione civile, soccorso 118, servizio militare e formazione."
        path="/titoli"
      />
      <Navbar />
      <section className="relative pt-32 pb-14 px-5 sm:px-8 overflow-hidden">
        <img src="/images/logo-watermark.png" alt="" aria-hidden="true"
          className="pointer-events-none select-none absolute -right-24 top-10 w-[30rem] max-w-none" />
        <div className="relative max-w-5xl mx-auto">
          <Link to="/" data-testid="attestati-back-home"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#3D5A66] hover:text-[#0284C7] transition-colors">
            <ArrowLeft size={15} /> {t.yurax.back}
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center gap-2 font-mono-ed text-xs uppercase tracking-[0.22em] text-[#0284C7]">
              <Award size={13} /> {a.label}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0B2530] mt-4 leading-[1.05]">
              {a.h1a}<em className="font-serif-ed italic font-medium text-[#06B6D4]">{a.h1em}</em>
            </h1>
            <p className="text-[#3D5A66] mt-6 max-w-2xl text-base sm:text-lg leading-relaxed">
              {a.intro}
            </p>
          </motion.div>
        </div>
      </section>

      {!unlocked ? (
        <section className="px-5 sm:px-8 max-w-4xl mx-auto pb-24" data-testid="attestati-lock">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl bg-white border border-[#DCEEF5] overflow-hidden grid grid-cols-1 md:grid-cols-2"
          >
            <div className="p-8 sm:p-10">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#FFF7ED] border border-[#FED7AA] px-4 py-1.5 text-xs font-mono-ed uppercase tracking-[0.14em] text-[#C2410C]">
                <Lock size={13} /> {a.lockLabel}
              </span>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B2530] mt-5">{a.lockTitle}</h2>
              <p className="text-sm sm:text-base text-[#3D5A66] mt-4 leading-relaxed">{a.lockText}</p>
              <form onSubmit={unlock} className="mt-6 space-y-4">
                <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="attestati-pw-input"
                  placeholder={a.pwPlaceholder} autoComplete="off"
                  className="w-full rounded-xl border border-[#DCEEF5] bg-[#FBFEFF] px-4 py-3 text-sm text-[#0B2530] focus:outline-none focus:border-[#0284C7] transition-colors" />
                {pwError && <p className="text-xs text-[#C2410C]" data-testid="attestati-unlock-error">{pwError}</p>}
                <button type="submit" disabled={pwBusy || !pw.trim()} data-testid="attestati-unlock-button"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0B2530] text-white font-semibold px-7 py-3.5 hover:bg-[#0284C7] transition-colors duration-300 active:scale-95 disabled:opacity-60">
                  <KeyRound size={15} /> {pwBusy ? "..." : a.unlockBtn}
                </button>
              </form>
            </div>
            <div className="p-8 sm:p-10 bg-gradient-to-br from-[#ECFEFF] to-[#F0F9FF] border-t md:border-t-0 md:border-l border-[#BAE6FD]">
              <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[#A5F3FC] px-4 py-1.5 text-xs font-mono-ed uppercase tracking-[0.14em] text-[#0369A1]">
                <Send size={12} /> {a.reqLabel}
              </span>
              <h3 className="font-display text-xl sm:text-2xl font-extrabold text-[#0B2530] mt-5">{a.reqTitle}</h3>
              <p className="text-sm text-[#3D5A66] mt-3 leading-relaxed">{a.reqText}</p>
              {reqDone ? (
                <p className="mt-6 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm font-semibold px-4 py-3" data-testid="attestati-req-success">
                  {a.reqSuccess}
                </p>
              ) : (
                <form onSubmit={sendAccessRequest} className="mt-5 space-y-3" data-testid="attestati-request-form">
                  <input type="text" value={reqName} onChange={(e) => setReqName(e.target.value)} required data-testid="attestati-req-name"
                    placeholder={t.contatti.phName}
                    className="w-full rounded-xl border border-[#DCEEF5] bg-white px-4 py-3 text-sm text-[#0B2530] focus:outline-none focus:border-[#0284C7] transition-colors" />
                  <input type="email" value={reqEmail} onChange={(e) => setReqEmail(e.target.value)} required data-testid="attestati-req-email"
                    placeholder={t.contatti.phEmail}
                    className="w-full rounded-xl border border-[#DCEEF5] bg-white px-4 py-3 text-sm text-[#0B2530] focus:outline-none focus:border-[#0284C7] transition-colors" />
                  <textarea value={reqMsg} onChange={(e) => setReqMsg(e.target.value)} rows={2} data-testid="attestati-req-message"
                    placeholder={a.reqMsg}
                    className="w-full rounded-xl border border-[#DCEEF5] bg-white px-4 py-3 text-sm text-[#0B2530] focus:outline-none focus:border-[#0284C7] transition-colors resize-none" />
                  <button type="submit" disabled={reqBusy || !reqName.trim() || !reqEmail.trim()} data-testid="attestati-req-submit"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0284C7] text-white font-semibold px-7 py-3.5 hover:bg-[#075E8A] transition-colors duration-300 active:scale-95 disabled:opacity-60">
                    <Send size={14} /> {reqBusy ? "..." : a.reqBtn}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </section>
      ) : (
        <>
      <section className="px-5 sm:px-8 max-w-4xl mx-auto pb-20" data-testid="attestati-featured">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl border-2 border-[#E4C97E] bg-gradient-to-br from-[#FFFBEF] to-[#FDF3D7] p-6 sm:p-10 text-center shadow-xl shadow-amber-200/30"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-white/80 border border-[#E4C97E] px-4 py-1.5 text-xs font-mono-ed uppercase tracking-[0.16em] text-[#8A6D1D]">
            <Medal size={13} /> {a.featuredLabel}
          </span>
          <div className="grid sm:grid-cols-2 gap-6 items-center mt-6">
            <button type="button" onClick={() => openZoom(featured)} data-testid="attestati-featured-zoom"
              onContextMenu={(e) => e.preventDefault()}
              className="block mx-auto cursor-zoom-in focus:outline-none group">
              <CertImage cert={featured}
                className="max-h-[60vh] w-auto mx-auto rounded-lg shadow-lg shadow-amber-900/10 group-hover:scale-[1.01] transition-transform duration-500" />
            </button>
            <div className="text-center">
              <button type="button" onClick={() => openZoom(featuredMedal)} data-testid="attestati-featured-medal"
                onContextMenu={(e) => e.preventDefault()}
                className="block mx-auto cursor-zoom-in focus:outline-none group">
                <img src={featuredMedal.img} alt="Medaglia NATO Non Article 5 — foto originale" loading="lazy" draggable={false}
                  className="max-h-[42vh] w-auto mx-auto rounded-xl border border-[#E4C97E] shadow-md shadow-amber-900/10 pointer-events-none select-none group-hover:scale-[1.02] transition-transform duration-500" />
              </button>
              <p className="text-xs text-[#8A6D1D] mt-3 italic">{a.medalCaption}</p>
            </div>
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-extrabold text-[#0B2530] mt-6">{featured.title}</h2>
          <p className="text-sm text-[#8A6D1D] mt-1.5">{featured.org} · {featured.year}</p>
        </motion.div>
      </section>

      {groups.map(({ g, items }) => {
        const visible = visibleMap[g] ?? PAGE_SIZE;
        return (
          <section key={g} className="px-5 sm:px-8 max-w-6xl mx-auto pb-16" data-testid={`attestati-group-0${g + 1}`}>
            <div className="flex items-center gap-4 mb-8">
              <h2 className="font-display text-xl sm:text-2xl font-extrabold text-[#0B2530] shrink-0">{a.groups[g]}</h2>
              <div className="h-px flex-1 bg-gradient-to-r from-[#A5F3FC] to-transparent" aria-hidden="true" />
              <span className="font-mono-ed text-xs text-[#5F7E8A]">{items.length}</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-7">
              {items.slice(0, visible).map((c, i) => (
                <motion.figure
                  key={c.img}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.7, delay: (i % PAGE_SIZE) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="relative rounded-2xl bg-white border border-[#DCEEF5] overflow-hidden select-none hover:shadow-xl hover:shadow-sky-500/10 hover:-translate-y-1 transition-all duration-300 cursor-zoom-in"
                  onClick={() => openZoom(c)}
                  onContextMenu={(e) => e.preventDefault()}
                  data-testid={`attestato-card-g${g}-${i + 1}`}
                >
                  <div className="relative aspect-[3/4] bg-[#FBFEFF] flex items-center justify-center p-4">
                    <CertImage cert={c} className="max-h-full max-w-full" />
                    <div className="absolute inset-0" aria-hidden="true" />
                    <span className="absolute top-3 right-3 rounded-full bg-white/90 border border-[#DCEEF5] p-1.5 text-[#0284C7] opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true">
                      <ZoomIn size={13} />
                    </span>
                  </div>
                  <figcaption className="px-4 sm:px-5 py-4 border-t border-[#DCEEF5]">
                    <div className="text-sm font-semibold text-[#0B2530] leading-snug">{c.title}</div>
                    <div className="text-xs text-[#5F7E8A] mt-1.5">{[c.org, c.year].filter(Boolean).join(" · ")}</div>
                  </figcaption>
                </motion.figure>
              ))}
            </div>
            {visible < items.length && (
              <div className="text-center mt-10">
                <button onClick={() => setVisibleMap((m) => ({ ...m, [g]: (m[g] ?? PAGE_SIZE) + PAGE_SIZE }))} data-testid={`attestati-more-button-g${g}`}
                  className="group inline-flex items-center gap-2 rounded-full bg-[#0B2530] text-white font-semibold px-8 py-3.5 hover:bg-[#0284C7] transition-colors duration-300 active:scale-95">
                  <Plus size={16} className="transition-transform duration-300 group-hover:rotate-90" />
                  {a.more}
                </button>
              </div>
            )}
          </section>
        );
      })}

      <section className="px-5 sm:px-8 max-w-4xl mx-auto pb-16" data-testid="attestati-cv-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl bg-gradient-to-br from-[#ECFEFF] to-[#F0F9FF] border border-[#BAE6FD] p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-10 text-center sm:text-left"
        >
          <div className="flex-1">
            <h2 className="font-display text-xl sm:text-2xl font-extrabold text-[#0B2530]">{a.cvTitle}</h2>
            <p className="text-sm sm:text-base text-[#3D5A66] mt-3 leading-relaxed">{a.cvText}</p>
          </div>
          <a href="/cv-yuri-selvaggio-2026.pdf" download="CV-Yuri-Selvaggio-2026.pdf" data-testid="attestati-cv-download"
            className="group shrink-0 inline-flex items-center gap-2 rounded-full bg-[#0284C7] text-white font-semibold px-7 py-3.5 hover:bg-[#075E8A] transition-colors duration-300 active:scale-95 shadow-lg shadow-sky-500/20">
            <FileDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            {a.cvBtn}
          </a>
        </motion.div>
      </section>

      <p className="flex items-center justify-center gap-2 text-xs text-[#5F7E8A] pb-16 px-5" data-testid="attestati-note">
        <Lock size={12} /> {a.note}
      </p>
        </>
      )}

      {zoomCert && (
        <div className="fixed inset-0 z-[100] bg-[#06202B]/90 backdrop-blur-sm flex flex-col"
          onContextMenu={(e) => e.preventDefault()} data-testid="attestato-modal">
          <div className="flex items-center justify-between gap-3 px-5 py-4 text-white">
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{zoomCert.title}</div>
              <div className="text-xs text-white/60 truncate">{zoomCert.org} · {zoomCert.year}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => setScale((s) => Math.max(1, s - 0.5))} data-testid="attestato-zoom-out" aria-label="Zoom out"
                className="rounded-full bg-white/10 hover:bg-white/20 p-2.5 transition-colors disabled:opacity-40" disabled={scale <= 1}>
                <ZoomOut size={17} />
              </button>
              <button onClick={() => setScale((s) => Math.min(3, s + 0.5))} data-testid="attestato-zoom-in" aria-label="Zoom in"
                className="rounded-full bg-white/10 hover:bg-white/20 p-2.5 transition-colors disabled:opacity-40" disabled={scale >= 3}>
                <ZoomIn size={17} />
              </button>
              <button onClick={closeZoom} data-testid="attestato-modal-close" aria-label="Chiudi"
                className="rounded-full bg-white/10 hover:bg-[#C2410C] p-2.5 transition-colors">
                <X size={17} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden flex items-center justify-center touch-none cursor-grab active:cursor-grabbing"
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}>
            <img src={zoomCert.img} alt={zoomCert.title} draggable={false}
              className="max-h-[82vh] max-w-[94vw] object-contain select-none pointer-events-none"
              style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})` }} />
          </div>
          <p className="text-center text-xs text-white/50 pb-5 px-5">{a.zoomHint}</p>
        </div>
      )}
      <Footer />
    </main>
  );
}
